import { lazy, Suspense, useState, type CSSProperties } from "react";
import { Info } from "lucide-react";
import { ProductionLineCanvas } from "@/features/lines/canvas/ProductionLineCanvas";
import { CanvasFullscreenModal } from "@/features/lines/canvas/CanvasFullscreenModal";
import { OrphanMachinesBanner } from "@/features/lines/canvas/OrphanMachinesBanner";
import { useAuthStore } from "@/stores/auth.store";
import { Notify, toast } from "@/components/ui/notify";
import { useLine } from "@/features/lines/hooks";
import { useCreateMachine } from "@/features/line-editor/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { SceneSkeleton } from "@/features/lines/scene/SceneSkeleton";
import { useSceneViewSwitch } from "@/features/lines/scene/hooks/useSceneViewSwitch";
import type { MachineDetail } from "@/features/lines/hooks";
import {
  FlowBandHeader,
  FlowSidebarBlock,
  type PendingAssignmentValue,
  type SidebarStateValue,
} from "./_flow-band-parts";

// Spec 078 FR-033 — o motor 3D (~270 KB gzipped) só entra por aqui, sob demanda. Quem fica no
// 2D nunca baixa o chunk; sem WebGL o chunk nem é pedido (o toggle some).
const LineScene = lazy(() =>
  import("@/features/lines/scene/LineSceneEntry").then((m) => ({ default: m.LineScene }))
);

const CANVAS_HEIGHT = 488;

export interface FlowSectionCardProps {
  lineId: string;
  lineName: string;
  lineStatus: "RUNNING" | "STOPPED" | "PARTIAL" | "IDLE";
  onMachineClick: (m: MachineDetail) => void;
}

export function FlowSectionCard({
  lineId,
  lineName,
  lineStatus,
  onMachineClick,
}: FlowSectionCardProps) {
  const { data: line } = useLine(lineId);
  const [fullscreen, setFullscreen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [sidebarState, setSidebarState] = useState<SidebarStateValue | null>(null);
  const [pendingAssignment, setPendingAssignment] = useState<PendingAssignmentValue | null>(null);
  const [pendingMachineAdd, setPendingMachineAdd] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const createMachine = useCreateMachine(lineId);
  const me = useAuthStore((s) => s.user)!;
  const isGestor = me.role === "GESTOR";
  const isRunning = lineStatus === "RUNNING";
  const machineCount = line?.machineCount ?? 0;
  // Spec 078 — visão 2D/3D (preferência + WebGL + guarda de saída do modo de edição).
  const show3dWanted = !editMode;
  const { view, webgl, changeView } = useSceneViewSwitch(lineId, show3dWanted);
  const show3d = view === "3d" && show3dWanted;

  async function startEdit() {
    if (isRunning) {
      const ok = await Notify.confirm({
        variant: "danger",
        title: "Editar fluxo em produção",
        description: `Editar o fluxo de "${lineName}" enquanto a linha está em produção pode afetar leituras e OEE. Digite o nome da linha para confirmar.`,
        typeToConfirm: lineName,
        confirmLabel: "Entrar em modo edição",
      });
      if (!ok) return;
    }
    setEditMode(true);
  }

  return (
    <section
      aria-labelledby="flow-section-card-h2"
      className="-mx-4 border-y border-border-subtle bg-bg-band px-4 py-[22px] pb-[26px] md:-mx-6 md:px-6"
    >
      <div>
        <FlowBandHeader
          machineCount={machineCount}
          editMode={editMode}
          isGestor={isGestor}
          onFullscreen={() => setFullscreen(true)}
          onStartEdit={() => void startEdit()}
          view={view}
          onViewChange={changeView}
          sceneAvailable={webgl}
          lineId={lineId}
          lineName={lineName}
        />

        {editMode ? (
          <div className="mb-2">
            <OrphanMachinesBanner lineId={lineId} />
          </div>
        ) : null}

        {show3d ? (
          <div
            className="h-[var(--scene-h)] overflow-hidden rounded-2xl border border-border-subtle"
            style={{ "--scene-h": `${CANVAS_HEIGHT}px` } as CSSProperties}
          >
            <Suspense fallback={<SceneSkeleton />}>
              <LineScene
                lineId={lineId}
                mode="view"
                onMachineClick={onMachineClick}
                height={CANVAS_HEIGHT}
              />
            </Suspense>
          </div>
        ) : null}
        <div
          className="overflow-hidden rounded-2xl border border-border-subtle bg-[#fbfaf8] [background-image:radial-gradient(circle_at_1px_1px,#dcd8d2_1px,transparent_0)] [background-size:18px_18px] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.02)]"
          hidden={show3d}
        >
          <ProductionLineCanvas
            lineId={lineId}
            mode={editMode ? "edit" : "display"}
            onMachineClick={editMode ? undefined : onMachineClick}
            height={488}
            isRunning={isRunning}
            onSaved={() => setEditMode(false)}
            onCancel={() => setEditMode(false)}
            onOpenSidebar={(node, options) => {
              setSidebarState({
                nodeId: node.id,
                machineId: (node.data?.machineId as string | undefined) ?? null,
                canvasAdd: options?.canvasAdd === true,
              });
            }}
            pendingMachineAssignment={pendingAssignment}
            onAssignmentApplied={() => setPendingAssignment(null)}
            onCreateNewMachine={
              editMode
                ? (basic) => {
                    void (async () => {
                      try {
                        const created = await createMachine.mutateAsync({
                          name: basic.name,
                          code: basic.code,
                          type: basic.type,
                          requiresOperator: false,
                        });
                        setPendingMachineAdd(created);
                        toast.success(`Máquina "${created.name}" cadastrada.`);
                      } catch (err) {
                        const msg = getFriendlyErrorMessage(err);
                        toast.error("Falha ao cadastrar máquina.", { description: msg });
                      }
                    })();
                  }
                : undefined
            }
            pendingMachineAdd={pendingMachineAdd}
            onMachineAddApplied={() => setPendingMachineAdd(null)}
          />
        </div>

        {}
        <p className="mt-3 flex items-center gap-1.5 text-[12px] text-text-secondary">
          <Info className="h-[13px] w-[13px]" aria-hidden="true" />
          {editMode
            ? "Modo edição: arraste nós, conecte arrastando das bordas, selecione e edite no painel lateral. Salve para persistir."
            : show3d
              ? "Passe o mouse numa máquina para ver os detalhes e clique para inspecionar. Arraste para orbitar, role para aproximar do cursor, botão direito para deslocar."
              : "Clique em uma máquina para inspecionar dados em tempo real. Use os controles para zoom."}
        </p>
      </div>

      <CanvasFullscreenModal
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        lineId={lineId}
        lineName={lineName}
        onMachineClick={onMachineClick}
      />

      <FlowSidebarBlock
        lineId={lineId}
        sidebarState={sidebarState}
        onClose={() => setSidebarState(null)}
        onCreated={(assignment) => {
          setPendingAssignment(assignment);
          setSidebarState((prev) =>
            prev ? { ...prev, machineId: assignment.machineId, canvasAdd: false } : null
          );
        }}
      />
    </section>
  );
}
